import type { LocationRecord } from '@/shared/repositories/locations/locationsRepository'
import type { ShiftTimeEntryRecord } from '@/shared/repositories/shiftTime/shiftTimeEntriesRepository'
import { matchLocationId } from '@/shared/lib/locationMatch'

export type ShiftAnalyticsEvent = {
  id: string
  locationId: string | null
  /** Nome textual do local na agenda (quando `locationId` ainda não foi gravado). */
  locationText?: string | null
  startDateTime: string
  endDateTime: string
}

export type ShiftAnalyticsInput = {
  entries: ShiftTimeEntryRecord[]
  events: ShiftAnalyticsEvent[]
  locations: LocationRecord[]
  globalRange: { start: Date; end: Date }
  locationRanges?: Record<string, { start: Date; end: Date }>
  includedLocationIds: string[]
}

/** Bucket key para entries/eventos sem local resolvido. */
const NO_LOCATION_KEY = '__sem_local__'

export type ShiftDayBreakdown = {
  date: string
  hours: number
  estimatedCents: number
}

export type ShiftAnalyticsResult = {
  totalHours: number
  totalEstimatedCents: number
  /** Plantões encerrados (registros + agenda sem registro) que somaram horas. */
  totalShiftCount: number
  openShift: { startedAt: string; locationName: string } | null
  byLocation: Array<{
    locationId: string
    name: string
    hours: number
    estimatedCents: number
    shiftCount: number
    /** Soma dos atendimentos informados (inclui o plantão em andamento); `null` sem nenhum informado. */
    attendanceCount: number | null
    openShift?: { startedAt: string }
    byDay: ShiftDayBreakdown[]
  }>
  chartSeries: { labels: string[]; hours: number[]; amountsCents: number[]; keys: string[] }
}

const MS_PER_HOUR = 3_600_000

type DayBucket = { hours: number; estimatedCents: number }

/** Chave YYYY-MM-DD no fuso local do navegador (pt-BR na UI). */
export const toLocalShiftDateKey = (iso: string): string => {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const formatShiftDayLabel = (dateKey: string): string => {
  const [year, month, day] = dateKey.split('-').map(Number)
  if (!year || !month || !day) return dateKey
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  })
}

const PT_WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const

/** Rótulo compacto para eixo do gráfico diário (ex.: 05-Seg). */
export const formatShiftDayChartLabel = (dateKey: string): string => {
  const [year, month, day] = dateKey.split('-').map(Number)
  if (!year || !month || !day) return dateKey
  const date = new Date(year, month - 1, day)
  const weekday = PT_WEEKDAY_SHORT[date.getDay()] ?? '—'
  return `${String(day).padStart(2, '0')}-${weekday}`
}

const addDayBucket = (
  map: Map<string, Map<string, DayBucket>>,
  locationKey: string,
  startedAtIso: string,
  hours: number,
  estimatedCents: number,
) => {
  const dateKey = toLocalShiftDateKey(startedAtIso)
  const byDate = map.get(locationKey) ?? new Map<string, DayBucket>()
  const prev = byDate.get(dateKey) ?? { hours: 0, estimatedCents: 0 }
  byDate.set(dateKey, {
    hours: prev.hours + hours,
    estimatedCents: prev.estimatedCents + estimatedCents,
  })
  map.set(locationKey, byDate)
}

const dayBucketsToSortedArray = (byDate: Map<string, DayBucket> | undefined): ShiftDayBreakdown[] =>
  [...(byDate?.entries() ?? [])]
    .map(([date, stats]) => ({ date, ...stats }))
    .sort((a, b) => a.date.localeCompare(b.date, 'pt-BR'))

/** Registros fechados com menos de 15 min não bloqueiam o fallback pela agenda. */
const MIN_CLOSED_ENTRY_HOURS_TO_BLOCK_EVENT = 0.25

export const resolveLocationIdForShift = (
  locationId: string | null | undefined,
  locationText: string | null | undefined,
  locations: LocationRecord[],
): string | null => {
  const direct = normalizeLocationId(locationId)
  if (direct) return direct

  const candidates = locations.map((loc) => ({
    id: loc.id,
    name: loc.name,
    address: loc.address ?? null,
  }))

  const text = locationText?.trim()
  if (!text) return null

  const match = matchLocationId(candidates, text)
  return match.locationId
}

const hoursBetween = (startIso: string, endIso: string): number =>
  Math.max(0, (new Date(endIso).getTime() - new Date(startIso).getTime()) / MS_PER_HOUR)

const inRange = (instant: Date, range: { start: Date; end: Date }): boolean =>
  instant >= range.start && instant < range.end

/** Normaliza IDs vazios vindos do banco/API para null. */
const normalizeLocationId = (locationId: string | null | undefined): string | null => {
  if (locationId == null) return null
  const trimmed = locationId.trim()
  return trimmed.length > 0 ? trimmed : null
}

const intersectRanges = (
  a: { start: Date; end: Date },
  b: { start: Date; end: Date },
): { start: Date; end: Date } | null => {
  const start = new Date(Math.max(a.start.getTime(), b.start.getTime()))
  const end = new Date(Math.min(a.end.getTime(), b.end.getTime()))
  if (start >= end) return null
  return { start, end }
}

/**
 * Período efetivo para um local.
 * - Override manual contido no global → interseção (filtro fino na PlantoesPage).
 * - Janela de corte de pagamento parcialmente fora do global → globalRange (horas do período UI).
 */
const resolveRangeForLocation = (
  locationId: string | null,
  input: ShiftAnalyticsInput,
): { start: Date; end: Date } => {
  const global = input.globalRange
  const specific = locationId ? input.locationRanges?.[locationId] : undefined
  if (!specific) return global

  const fullyWithinGlobal =
    specific.start >= global.start && specific.end <= global.end
  if (fullyWithinGlobal) {
    return intersectRanges(global, specific) ?? global
  }

  if (!intersectRanges(global, specific)) return global
  return global
}

export const computeShiftAnalytics = (input: ShiftAnalyticsInput): ShiftAnalyticsResult => {
  const locationById = new Map(input.locations.map((loc) => [loc.id, loc]))
  const included = new Set(input.includedLocationIds)
  const eventById = new Map(input.events.map((ev) => [ev.id, ev]))

  const eventEffectiveLocationId = (event: ShiftAnalyticsEvent): string | null =>
    resolveLocationIdForShift(event.locationId, event.locationText ?? null, input.locations)

  type LocationStats = {
    hours: number
    estimatedCents: number
    shiftCount: number
    attendanceCount: number | null
    openShift?: { startedAt: string }
  }
  const emptyStats = (): LocationStats => ({ hours: 0, estimatedCents: 0, shiftCount: 0, attendanceCount: null })
  const byLocationMap = new Map<string, LocationStats>()
  const addAttendance = (stats: LocationStats, count: number | null | undefined): number | null =>
    count == null ? stats.attendanceCount : (stats.attendanceCount ?? 0) + count
  const dailyByLocationMap = new Map<string, Map<string, DayBucket>>()

  let totalHours = 0
  let totalEstimatedCents = 0
  let totalShiftCount = 0
  let openShift: ShiftAnalyticsResult['openShift'] = null

  /**
   * Resolve o local efetivo de uma entry:
   * entry.locationId ?? evento(entry.eventId)?.locationId
   */
  const resolveEffectiveLocationId = (entry: ShiftTimeEntryRecord): string | null => {
    const direct = normalizeLocationId(entry.locationId)
    if (direct) return direct
    if (!entry.eventId) return null
    const event = eventById.get(entry.eventId)
    if (!event) return null
    return eventEffectiveLocationId(event)
  }

  /**
   * Verifica se uma entry (pelo local efetivo) está inclusa nos filtros.
   * Entries sem local efetivo sempre entram (horas contam sempre).
   */
  const isEntryIncluded = (effectiveLocationId: string | null): boolean => {
    if (!effectiveLocationId) return true
    return included.has(effectiveLocationId)
  }

  for (const entry of input.entries) {
    const effectiveLocationId = resolveEffectiveLocationId(entry)
    if (!isEntryIncluded(effectiveLocationId)) continue

    const range = resolveRangeForLocation(effectiveLocationId, input)
    if (!inRange(new Date(entry.startedAt), range)) continue

    const loc = effectiveLocationId ? locationById.get(effectiveLocationId) : null
    const bucketKey = effectiveLocationId ?? NO_LOCATION_KEY

    if (!entry.endedAt) {
      const current = byLocationMap.get(bucketKey) ?? emptyStats()
      byLocationMap.set(bucketKey, {
        ...current,
        attendanceCount: addAttendance(current, entry.attendanceCount),
        openShift: { startedAt: entry.startedAt },
      })
      if (!openShift) {
        openShift = { startedAt: entry.startedAt, locationName: loc?.name ?? 'Sem local' }
      }
      continue
    }

    const hours = hoursBetween(entry.startedAt, entry.endedAt)
    if (hours < MIN_CLOSED_ENTRY_HOURS_TO_BLOCK_EVENT) continue
    const cents = loc?.isRemunerated ? Math.round(hours * (loc.hourlyRateCents ?? 0)) : 0
    totalHours += hours
    totalEstimatedCents += cents
    totalShiftCount += 1
    addDayBucket(dailyByLocationMap, bucketKey, entry.startedAt, hours, cents)

    const prev = byLocationMap.get(bucketKey) ?? emptyStats()
    byLocationMap.set(bucketKey, {
      hours: prev.hours + hours,
      estimatedCents: prev.estimatedCents + cents,
      shiftCount: prev.shiftCount + 1,
      attendanceCount: addAttendance(prev, entry.attendanceCount),
      openShift: prev.openShift,
    })
  }

  const closedHoursByEvent = new Map<string, number>()
  for (const entry of input.entries) {
    if (!entry.eventId || !entry.endedAt) continue
    const hours = hoursBetween(entry.startedAt, entry.endedAt)
    closedHoursByEvent.set(
      entry.eventId,
      (closedHoursByEvent.get(entry.eventId) ?? 0) + hours,
    )
  }

  const eventsWithMeaningfulEntry = new Set(
    [...closedHoursByEvent.entries()]
      .filter(([, hours]) => hours >= MIN_CLOSED_ENTRY_HOURS_TO_BLOCK_EVENT)
      .map(([eventId]) => eventId),
  )

  for (const event of input.events) {
    if (eventsWithMeaningfulEntry.has(event.id)) continue

    const effectiveLocationId = eventEffectiveLocationId(event)
    if (!effectiveLocationId) continue
    if (!included.has(effectiveLocationId)) continue

    const loc = effectiveLocationId ? locationById.get(effectiveLocationId) : null

    const range = resolveRangeForLocation(effectiveLocationId, input)
    const start = new Date(event.startDateTime)
    if (!inRange(start, range)) continue

    const hours = hoursBetween(event.startDateTime, event.endDateTime)
    const cents = loc?.isRemunerated ? Math.round(hours * (loc.hourlyRateCents ?? 0)) : 0
    totalHours += hours
    totalEstimatedCents += cents
    totalShiftCount += 1
    addDayBucket(dailyByLocationMap, effectiveLocationId, event.startDateTime, hours, cents)

    const prev = byLocationMap.get(effectiveLocationId) ?? emptyStats()
    byLocationMap.set(effectiveLocationId, {
      ...prev,
      hours: prev.hours + hours,
      estimatedCents: prev.estimatedCents + cents,
      shiftCount: prev.shiftCount + 1,
    })
  }

  const byLocation = [...byLocationMap.entries()]
    .map(([key, stats]) => ({
      locationId: key,
      name: key === NO_LOCATION_KEY ? 'Sem local' : (locationById.get(key)?.name ?? 'Local'),
      ...stats,
      byDay: dayBucketsToSortedArray(dailyByLocationMap.get(key)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

  return {
    totalHours,
    totalEstimatedCents,
    totalShiftCount,
    openShift,
    byLocation,
    chartSeries: {
      labels: byLocation.map((row) => row.name),
      hours: byLocation.map((row) => row.hours),
      amountsCents: byLocation.map((row) => row.estimatedCents),
      keys: byLocation.map((row) => row.locationId),
    },
  }
}

export const formatShiftAmountBrl = (cents: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)
